import { useGetDepartmentsQuery } from '../../Shippings/services/Addresses';
import { Option } from '@/components/inputs/AutocompleteInput';
import { useEffect, useMemo, useState } from 'react';

interface UseDepartmentSearchInputProps {
  departmentId?: string;
  skip?: boolean;
}

const useDepartmentSearchInput = ({
  departmentId,
  skip = false,
}: UseDepartmentSearchInputProps) => {
  const [selectedDepartment, setSelectedDepartment] = useState<Option | null>(null);
  const [departmentSearch, setDepartmentSearch] = useState('');

  const { data: departmentsData, isLoading: isDepartmentsLoading } =
    useGetDepartmentsQuery(
      { paginated: { page: 1, pageSize: 50 }, search: departmentSearch },
      { skip: skip || departmentSearch.length < 3 }
    );

  const departmentOptions: Option[] = useMemo(
    () =>
      (departmentsData?.items ?? []).map((dept) => ({
        label: dept.name,
        data: dept.id,
      })),
    [departmentsData]
  );

  const departmentIdForLookup = departmentId?.trim() ?? '';

  const { data: allDepartments } = useGetDepartmentsQuery(
    { paginated: { page: 1, pageSize: 50 }, search: '' },
    { skip: skip || !departmentIdForLookup || !!selectedDepartment }
  );

  useEffect(() => {
    if (!selectedDepartment && departmentIdForLookup && allDepartments?.items?.length) {
      const dept = allDepartments.items.find((d) => d.id === departmentIdForLookup);
      if (dept) {
        setSelectedDepartment({ label: dept.name, data: dept.id });
      }
    }
  }, [allDepartments, departmentIdForLookup, selectedDepartment]);

  useEffect(() => {
    if (!departmentId) setSelectedDepartment(null);
  }, [departmentId]);

  const handleDepartmentSelect = (option: Option | null) => {
    setSelectedDepartment(option);
  };

  const clearDepartment = () => {
    setDepartmentSearch('');
    setSelectedDepartment(null);
  };

  return {
    selectedDepartment,
    departmentSearch,
    setDepartmentSearch,
    departmentOptions,
    isDepartmentsLoading,
    handleDepartmentSelect,
    clearDepartment,
  };
};

export default useDepartmentSearchInput;
